import { useEffect } from 'react';
import { RecordSearchTermOptions, useRecordSearchTerm } from './use-record-search-term';

export type SearchTermRecordRequest = Omit<RecordSearchTermOptions, 'resultCount'>;

export type UseRecordSearchTermAfterResultsOptions = {
    // The search terms the current results are for, guards against recording the results of a previous search
    activeSearchTerms: ReadonlyArray<string>;
    isLoading: boolean;
    // Called once the request has been recorded, the caller should clear the request
    onRecorded: () => void;
    // Set by the caller when a search is submitted, null when there is nothing to record
    request: SearchTermRecordRequest | null;
    // The exact total for the search, null or undefined when unknown, for example when the search failed
    totalCount: number | null | undefined;
};

/**
 * Records a submitted search term once its results have loaded, using the exact search total as the result count.
 *
 * The caller owns the pending request so that the search can be submitted in a different component to the one
 * that loads the results. Only submitted searches create a request, so pagination, sorting and refetches never
 * record. When the results fail to load the search term is recorded with a null result count.
 */
export function useRecordSearchTermAfterResults({
    activeSearchTerms,
    isLoading,
    onRecorded,
    request,
    totalCount,
}: UseRecordSearchTermAfterResultsOptions): void {
    const recordSearchTerm = useRecordSearchTerm();

    useEffect(() => {
        if (!request || isLoading || !activeSearchTerms.includes(request.searchTerm)) {
            return;
        }
        recordSearchTerm({ ...request, resultCount: totalCount ?? null });
        onRecorded();
    }, [activeSearchTerms, isLoading, onRecorded, recordSearchTerm, request, totalCount]);
}
